import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  Student,
  StudentEditResource,
  StudentListDto,
  StudentListFilter,
  StudentServicePartialStudent,
  StudentServiceTemplate,
  StudentServiceTemplateAttendee,
  StudentServiceTemplateEditResource,
  StudentServiceTemplateViewResource,
  StudentViewResource,
} from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentLinkPageHeader } from "./StudentLinkPageHeader";
import { StudentLinkPageBody, StudentLinkTypeTab } from "./StudentLinkPageBody";
import { studentPageRoute } from "pages/students/students/StudentPage/StudentPage";
import { useInitListFilter } from "hooks/list/useInitListFilter";
import { createEntity } from "utils";

const routeName = "studentLink";

class Params {
  studentPartialId: number = 0;
  screenType: ScreenType = "dialog";
}

export const studentLinkPageRoute: PageRoute<Params> = {
  handle: "Student Linking",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

const ROOT_PERMISSION: Permission = Permission.StudentServiceTemplate;

export const StudentLinkPage = () => {
  const { params } = useParams(routeName, Params);

  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<StudentLinkTypeTab>("Existing Student");
  const [existingStudent, setExistingStudent] = useState<StudentListDto>();
  const [partialStudent, setPartialStudent] = useState<StudentServicePartialStudent>({} as StudentServicePartialStudent);
  const listContext = useContext<ListContextValue>(ListContext);
  const [viewResource, setViewResource] = useState<StudentServiceTemplateViewResource>({});
  const [editResource, setEditResource] = useState<StudentServiceTemplateEditResource>({});

  const [newStudent, setNewStudent] = useState<Student>();
  const [newStudentViewResource, setNewStudentViewResource] = useState<StudentViewResource>({});
  const [newStudentEditResource, setNewStudentEditResource] = useState<StudentEditResource>(Object);

  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [newStudent] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    getPartialStudent(params.studentPartialId);
    getViewResource();
  }, []);

  useEffect(() => {
    if (currentTab == "New Student") {
      if (!newStudent && partialStudent) {
        ApiService.students.get(0).then((response) => {
          const nameSplit = partialStudent.studentName!.split(" ");
          response.firstName = nameSplit[0];
          response.lastName = nameSplit.length == 3 ? nameSplit[2] : nameSplit.length == 2 ? nameSplit[1] : "";
          response.middleName = nameSplit.length == 3 ? nameSplit[1] : "";
          response.dateOfBirth = partialStudent.dateOfBirth
          setNewStudent(response);
        });
      }
    }
  }, [currentTab]);

  const getPartialStudent = (studentPartialId: number) => {
    ApiService.studentServiceTemplates.getPartialStudent(studentPartialId).then((response) => {
      setPartialStudent(response);
      resetUnsavedChanges([{}]);
    });
  };

  const getViewResource = () => {
    ApiService.studentServiceTemplates.getViewResource(0).then((response) => {
      setViewResource(response);
    });
    ApiService.students.getViewResource(0).then((response) => {
      setNewStudentViewResource(response);
    });
    ApiService.students.getEditResource().then((response) => {
      setNewStudentEditResource(response);
    });
  };

  const getEditResource = () => {
    ApiService.studentServiceTemplates.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentLinkPageHeader
      currentTab={currentTab}
      partialStudent={partialStudent}
      newStudent={newStudent}
      existingStudent={existingStudent}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
      onSave={(id) => {
        navigate(`../${studentPageRoute.getPath({ id: id, crudMode: "view" })}`);
        listContext.loadList?.();
      }}
    />
  );

  const pageBody = (
    <StudentLinkPageBody
      crudMode="edit"
      currentTab={currentTab}
      setCurrentTab={setCurrentTab}
      rootPermission={ROOT_PERMISSION}
      newStudent={newStudent}
      setNewStudent={setNewStudent}
      newStudentViewResource={newStudentViewResource}
      newStudentEditResource={newStudentEditResource}
      existingStudent={existingStudent}
      setExistingStudent={setExistingStudent}
      partialStudent={partialStudent}
      // studentServiceTemplate={studentServiceTemplate}
      // setStudentServiceTemplate={setStudentServiceTemplate}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <>
      <Page isForm header={pageHeader}>
          {pageBody}
        </Page>
    </>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="lg"
      header={pageHeader}
      content={pageBody}
    />
  );
};
